/**
 * Pure state machine for the WagePro migration-import wizard
 * (`employee_migration_screen.tsx`, wmi_plan §8). Kept out of the JSX so every
 * transition and the stepper projection are unit-testable in `bun:test` with
 * no React or DOM — the same "component holds JSX + wiring, logic holds
 * transforms" split `docs/testing-titanium-standard.md` mandates.
 *
 * No I/O, no fetch, no i18n resolution: the screen owns those. The wizard runs
 * seven steps (start → template → upload → map → review → import → done); `map`
 * is skipped automatically by the screen when every uploaded header already
 * matches, but it stays in the rail so the user always sees the full journey.
 */

import type { StepperStep, StepperStepStatus } from '@jumpstone/ui-react';

/** The seven wizard steps, in order. */
export const MIGRATION_STEP_ORDER = [
  'start',
  'template',
  'upload',
  'map',
  'review',
  'import',
  'done',
] as const;

export type MigrationStepId = (typeof MIGRATION_STEP_ORDER)[number];

/** What the user chose to bring across. */
export type MigrationScope = 'full' | 'payroll_only';

export interface WizardState {
  step: MigrationStepId;
  scope: MigrationScope;
  /** Cut-over date as `YYYY-MM-DD`, or `''` until the user picks one. */
  cutover_date: string;
  /** The staged/committing run id, once `stage` has created one. */
  run_id: string | null;
}

/**
 * A fresh wizard. `payroll_only` can be preselected (the old payroll-CSV
 * import redirect lands here with that scope) — otherwise `full`.
 */
export function initialWizardState(
  options: { scope?: MigrationScope } = {},
): WizardState {
  return {
    step: 'start',
    scope: options.scope ?? 'full',
    cutover_date: '',
    run_id: null,
  };
}

/** 0-based position of a step in {@link MIGRATION_STEP_ORDER}. */
export function stepIndex(step: MigrationStepId): number {
  return MIGRATION_STEP_ORDER.indexOf(step);
}

/** The stepper status of `step` relative to the `current` step. */
export function stepperStatusFor(
  step: MigrationStepId,
  current: MigrationStepId,
): StepperStepStatus {
  const a = stepIndex(step);
  const b = stepIndex(current);
  if (a < b) return 'done';
  if (a === b) return 'active';
  return 'upcoming';
}

/**
 * Project the full step order into {@link StepperStep}s for `<Stepper>`.
 * `labels` maps each step id to its already-translated label.
 */
export function buildStepperSteps(
  current: MigrationStepId,
  labels: Record<MigrationStepId, string>,
): StepperStep[] {
  return MIGRATION_STEP_ORDER.map((step) => ({
    id: step,
    label: labels[step],
    status: stepperStatusFor(step, current),
  }));
}

/** The next step, or the same step when already at the end. */
export function nextStep(step: MigrationStepId): MigrationStepId {
  const i = stepIndex(step);
  return MIGRATION_STEP_ORDER[Math.min(i + 1, MIGRATION_STEP_ORDER.length - 1)];
}

/** The previous step, or the same step when already at the start. */
export function prevStep(step: MigrationStepId): MigrationStepId {
  const i = stepIndex(step);
  return MIGRATION_STEP_ORDER[Math.max(i - 1, 0)];
}

/**
 * `YYYY-MM-DD` that is a real calendar date (so `2026-02-30` is rejected) and
 * not in the future relative to `today` (also `YYYY-MM-DD`). The cut-over is
 * the day you switch to WagePro — it cannot be tomorrow.
 */
export function isValidCutoverDate(value: string, today: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map((p) => Number.parseInt(p, 10));
  const date = new Date(Date.UTC(y, m - 1, d));
  // Round-trip guard: Date normalises 2026-02-30 → March, so compare back.
  if (
    date.getUTCFullYear() !== y ||
    date.getUTCMonth() !== m - 1 ||
    date.getUTCDate() !== d
  ) {
    return false;
  }
  return value <= today;
}

/** The Start step is complete once a valid, non-future cut-over date is set. */
export function canLeaveStart(state: WizardState, today: string): boolean {
  return isValidCutoverDate(state.cutover_date, today);
}

/**
 * Whether a run status means loading is finished. The import step polls
 * `commit` until this is true.
 */
export function isCommitComplete(status: string): boolean {
  return status === 'committed';
}

/**
 * Whether a run status is terminal in a way that is NOT a success — the import
 * step shows "Continue import" so the user can resume a run that stopped
 * part-way (it stays `loading`), and surfaces a hard failure.
 */
export function isCommitFailed(status: string): boolean {
  return status === 'failed' || status === 'aborted';
}

/**
 * One step of the chunked commit poll (pure, so the loop's control flow is
 * unit-tested without React): given the run status returned and whether
 * `loaded` advanced since the previous poll, decide what to do next.
 *  - `done`   → the run committed.
 *  - `failed` → the run is terminal-not-success (failed/aborted); a hard stop.
 *  - `stalled`→ still `loading` but `loaded` did not advance — stop polling and
 *               offer "Continue import" rather than spinning forever (F8/F9).
 *  - `continue` → keep polling.
 */
export type CommitAction = 'done' | 'failed' | 'stalled' | 'continue';

export function classifyCommitProgress(
  status: string,
  loaded: number,
  prevLoaded: number,
): CommitAction {
  if (isCommitComplete(status)) return 'done';
  if (isCommitFailed(status)) return 'failed';
  if (loaded <= prevLoaded) return 'stalled';
  return 'continue';
}

/**
 * Pick which step to resume into for an existing run found on mount. A
 * `loading` run resumes at `import`; a `committed`/terminal run resumes at
 * `done`; anything staged-but-not-committing resumes at `review`.
 */
export function resumeStepForStatus(status: string): MigrationStepId {
  if (status === 'loading') return 'import';
  if (status === 'committed') return 'done';
  if (status === 'failed' || status === 'aborted') return 'done';
  // dry_run_ready / mapping / awaiting_commit → back to review.
  return 'review';
}
